import React, { useState } from 'react';
import { Section } from '../layout/Section';

const WHATSAPP_NUMBER = '491781690543'; // international format without +
const EMAIL = 'fey-am-tibarg@gmx.de';

interface FormData {
  name: string;
  datum: string;
  uhrzeit: string;
  personen: string;
  nachricht: string;
}

export const ReservierungSection: React.FC = () => {
  const [form, setForm] = useState<FormData>({
    name: '',
    datum: '',
    uhrzeit: '',
    personen: '2',
    nachricht: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Check if selected date is Tuesday (2) or Wednesday (3) — closed days
  const isClosedDay = (dateStr: string): boolean => {
    if (!dateStr) return false;
    const day = new Date(dateStr).getDay(); // 0=Sun, 1=Mon, 2=Tue, 3=Wed
    return day === 2 || day === 3;
  };

  const closedDayError = form.datum && isClosedDay(form.datum)
    ? 'Dienstag und Mittwoch sind wir leider geschlossen. Bitte wählen Sie einen anderen Tag.'
    : null;

  // Build message text used for both WhatsApp and Email
  const buildMessage = () =>
    `Reservierungsanfrage Restaurant Fey\n\nName: ${form.name}\nDatum: ${form.datum}\nUhrzeit: ${form.uhrzeit} Uhr\nPersonen: ${form.personen}${form.nachricht ? `\nNachricht: ${form.nachricht}` : ''}`;

  const handleWhatsApp = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.datum || !form.uhrzeit) return;
    const text = encodeURIComponent(buildMessage());
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${text}`, '_blank', 'noopener,noreferrer');
  };

  const handleEmail = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.datum || !form.uhrzeit) return;
    const subject = encodeURIComponent(`Reservierungsanfrage – ${form.datum} – ${form.personen} Personen`);
    const body = encodeURIComponent(buildMessage());
    window.location.href = `mailto:${EMAIL}?subject=${subject}&body=${body}`;
  };

  const isValid = form.name.trim() !== '' && form.datum !== '' && form.uhrzeit !== '' && !isClosedDay(form.datum);

  // Min date = tomorrow (24h advance notice required)
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const today = tomorrow.toISOString().split('T')[0];

  return (
    <Section id="reservierung" variant="darker" aria-labelledby="reservierung-heading">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h2
            id="reservierung-heading"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
          >
            Tisch reservieren
          </h2>
          <div className="w-24 h-1 bg-[#c9a84c] mx-auto rounded-full" aria-hidden="true" />
          <p className="mt-4 text-gray-400 text-base max-w-md mx-auto">
            Reservieren Sie Ihren Tisch bequem per WhatsApp oder E-Mail – wir freuen uns auf Sie!
          </p>
          <p className="mt-3 text-sm text-[#c9a84c]/80 max-w-lg mx-auto leading-relaxed">
            Bitte beachten Sie: Reservierungen sind mindestens <span className="font-semibold text-[#c9a84c]">24 Stunden im Voraus</span> erforderlich und gelten erst nach unserer ausdrücklichen Bestätigung als verbindlich.
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-black/70 border border-[#c9a84c]/30 rounded-xl overflow-hidden shadow-2xl">
          <div className="h-1 bg-gradient-to-r from-transparent via-[#c9a84c] to-transparent" aria-hidden="true" />

          <form className="p-8 sm:p-10 space-y-6" noValidate>
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                Ihr Name <span className="text-[#c9a84c]">*</span>
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                value={form.name}
                onChange={handleChange}
                placeholder="Max Mustermann"
                className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-[#c9a84c]/60 focus:ring-1 focus:ring-[#c9a84c]/40 transition-colors"
              />
            </div>

            {/* Datum + Uhrzeit */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="datum" className="block text-sm font-medium text-gray-300 mb-2">
                  Datum <span className="text-[#c9a84c]">*</span>
                </label>
                <input
                  id="datum"
                  name="datum"
                  type="date"
                  required
                  min={today}
                  value={form.datum}
                  onChange={handleChange}
                  className={`w-full bg-black/40 border rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-1 transition-colors ${closedDayError ? 'border-red-500 focus:border-red-500 focus:ring-red-500/40' : 'border-white/10 focus:border-[#c9a84c]/60 focus:ring-[#c9a84c]/40'}`}
                  style={{ colorScheme: 'dark' }}
                />
                {closedDayError && (
                  <p className="mt-2 text-xs text-red-400" role="alert">
                    {closedDayError}
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="uhrzeit" className="block text-sm font-medium text-gray-300 mb-2">
                  Uhrzeit <span className="text-[#c9a84c]">*</span>
                </label>
                <select
                  id="uhrzeit"
                  name="uhrzeit"
                  required
                  value={form.uhrzeit}
                  onChange={handleChange}
                  className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#c9a84c]/60 focus:ring-1 focus:ring-[#c9a84c]/40 transition-colors"
                >
                  <option value="" className="bg-[#1a1a1a]">Bitte wählen</option>
                  {['12:00','12:30','13:00','13:30','14:00','14:30',
                    '17:00','17:30','18:00','18:30','19:00','19:30',
                    '20:00','20:30','21:00','21:30'].map(t => (
                    <option key={t} value={t} className="bg-[#1a1a1a]">{t} Uhr</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Personen */}
            <div>
              <label htmlFor="personen" className="block text-sm font-medium text-gray-300 mb-2">
                Anzahl Personen
              </label>
              <select
                id="personen"
                name="personen"
                value={form.personen}
                onChange={handleChange}
                className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#c9a84c]/60 focus:ring-1 focus:ring-[#c9a84c]/40 transition-colors"
              >
                {['1','2','3','4','5','6','7','8','9','10+'].map(n => (
                  <option key={n} value={n} className="bg-[#1a1a1a]">{n} {n === '1' ? 'Person' : 'Personen'}</option>
                ))}
              </select>
            </div>

            {/* Nachricht */}
            <div>
              <label htmlFor="nachricht" className="block text-sm font-medium text-gray-300 mb-2">
                Besondere Wünsche <span className="text-gray-500 font-normal">(optional)</span>
              </label>
              <textarea
                id="nachricht"
                name="nachricht"
                rows={3}
                value={form.nachricht}
                onChange={handleChange}
                placeholder="z. B. Allergien, Geburtstag, Kinderstuhl ..."
                className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-[#c9a84c]/60 focus:ring-1 focus:ring-[#c9a84c]/40 transition-colors resize-none"
              />
            </div>

            {/* Buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              {/* WhatsApp */}
              <button
                type="submit"
                onClick={handleWhatsApp}
                disabled={!isValid}
                className="flex items-center justify-center gap-3 px-6 py-4 rounded-lg font-semibold text-white bg-[#25D366] hover:bg-[#1ebe5a] disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:ring-offset-2 focus:ring-offset-[#111111]"
                aria-label="Reservierung per WhatsApp senden"
              >
                {/* WhatsApp icon */}
                <svg width={22} height={22} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Per WhatsApp
              </button>

              {/* Email */}
              <button
                type="button"
                onClick={handleEmail}
                disabled={!isValid}
                className="flex items-center justify-center gap-3 px-6 py-4 rounded-lg font-semibold text-[#1a1a1a] bg-[#c9a84c] hover:bg-[#d4b55e] disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-[#c9a84c] focus:ring-offset-2 focus:ring-offset-[#111111]"
                aria-label="Reservierung per E-Mail senden"
              >
                {/* Email icon */}
                <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                Per E-Mail
              </button>
            </div>

            <p className="text-xs text-gray-500 text-center pt-1 leading-relaxed">
              * Pflichtfelder. Ihre Anfrage wird direkt an uns weitergeleitet.<br />
              Ihre Reservierung ist erst nach unserer Bestätigung verbindlich.
            </p>
          </form>

          <div className="h-1 bg-gradient-to-r from-transparent via-[#c9a84c] to-transparent" aria-hidden="true" />
        </div>
      </div>
    </Section>
  );
};
